import React from "react";
import { styled } from "@mui/material/styles";
import ClosingCard from "./ClosingCard";
import InvestigatingCard from "./InvestigatingCard";
import AwaitingActionCard from "./AwaitingActionCard";
import AwaitingUpdateCard from "./AwaitingUpdateCard";

const Card = ({
  lead,
  leadStatusFilter,
  cardIndex,
  userId,
  userFullName,
  dashPerspective,
  handleOpenLeadActionsModal,
  teamId,
}) => {
  const mortgage = lead.belongsToMortgage;
  const primaryAddress = getPrimaryAddress(mortgage);
  const borrowerLabel = getBorrowerLabel(mortgage);
  const statusClassName = getStatusClassName(leadStatusFilter);

  return (
    <CardFrame>
      <CardAccent className={statusClassName} />

      <CardInner>
        <CardSummary>
          <SummaryPrimary>
            <StatusBadge className={statusClassName}>
              {getStatusLabel(leadStatusFilter)}
            </StatusBadge>

            <SummaryTitle>{primaryAddress}</SummaryTitle>
            <SummarySubtitle>{borrowerLabel}</SummarySubtitle>
          </SummaryPrimary>

          <SummaryMetrics>
            <MetricBlock>
              <span>Tier</span>
              <strong>{getTierLabel(lead.tier)}</strong>
            </MetricBlock>
            <MetricBlock>
              <span>Active</span>
              <strong>{mortgage.activeDiscrepancies || 0}</strong>
            </MetricBlock>
            <MetricBlock>
              <span>Assignees</span>
              <strong>{lead.assigneeNames.length}</strong>
            </MetricBlock>
            <MetricBlock>
              <span>Outcome</span>
              <strong>{getOutcomeLabel(lead.targetOutcome)}</strong>
            </MetricBlock>
            <MetricBlock>
              <span>Discovered</span>
              <strong>{lead.dateDiscoveredLabel || "N/A"}</strong>
            </MetricBlock>
          </SummaryMetrics>
        </CardSummary>

        <CardBody>
          {leadStatusFilter === "Awaiting Action" ? (
            <AwaitingActionCard
              lead={lead}
              handleOpenLeadActionsModal={handleOpenLeadActionsModal}
              cardIndex={cardIndex}
            />
          ) : leadStatusFilter === "Investigating" ? (
            <InvestigatingCard
              lead={lead}
              userId={userId}
              userFullName={userFullName}
              dashPerspective={dashPerspective}
              handleOpenLeadActionsModal={handleOpenLeadActionsModal}
              cardIndex={cardIndex}
            />
          ) : leadStatusFilter === "Closing" ? (
            <ClosingCard
              lead={lead}
              userId={userId}
              userFullName={userFullName}
              dashPerspective={dashPerspective}
              handleOpenLeadActionsModal={handleOpenLeadActionsModal}
              cardIndex={cardIndex}
            />
          ) : (
            <AwaitingUpdateCard
              lead={lead}
              userId={userId}
              userFullName={userFullName}
              dashPerspective={dashPerspective}
              handleOpenLeadActionsModal={handleOpenLeadActionsModal}
              cardIndex={cardIndex}
              teamId={teamId}
            />
          )}
        </CardBody>
      </CardInner>
    </CardFrame>
  );
};

const getStatusClassName = (leadStatusFilter) => {
  if (leadStatusFilter === "Awaiting Action") return "awaitingAction";
  if (leadStatusFilter === "Investigating") return "investigating";
  if (leadStatusFilter === "Closing") return "closing";
  return "awaitingUpdate";
};

const getStatusLabel = (leadStatusFilter) => {
  if (leadStatusFilter === "Awaiting PR Updates") return "Awaiting public record update";
  return leadStatusFilter;
};

const getTierLabel = (tier) => {
  if (tier === 0 || tier === 5) return "Manual";
  return `Tier ${tier}`;
};

const getOutcomeLabel = (targetOutcome) => {
  if (targetOutcome === "renegotiation") return "Renegotiation";
  if (targetOutcome === "refinance") return "Refinance";
  return "Pending";
};

const getPrimaryAddress = (mortgage) => {
  if (mortgage.streetAddress) {
    return mortgage.streetAddress;
  }

  return "Mortgage address unavailable";
};

const getBorrowerLabel = (mortgage) => {
  if (mortgage.owner1 && mortgage.owner2) {
    return `${mortgage.owner1} / ${mortgage.owner2}`;
  }

  if (mortgage.owner1) return mortgage.owner1;
  if (mortgage.owner2) return mortgage.owner2;
  return "Borrower unavailable";
};

const CardFrame = styled("article")(({ theme }) => ({
  position: "relative",
  width: "100%",
  minWidth: "88rem",
  display: "grid",
  gridTemplateColumns: "0.45rem 1fr",
  overflow: "hidden",
  borderRadius: "0.8rem",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  background: "#fff",
  boxShadow: "0 0.8rem 1.9rem rgba(15, 23, 42, 0.075)",
  transition:
    "transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease",

  "&:hover": {
    transform: "translateY(-0.15rem)",
    borderColor: "rgba(37,99,235,0.24)",
    boxShadow: "0 1rem 2.4rem rgba(15, 23, 42, 0.11)",
  },

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0) 22%)",
  },
}));

const CardAccent = styled("div")({
  gridColumn: 1,
  width: "100%",
  height: "100%",

  "&.awaitingAction": {
    background: "linear-gradient(180deg, #c2410c, #9a3412)",
  },

  "&.investigating": {
    background: "linear-gradient(180deg, #2563eb, #1e40af)",
  },

  "&.closing": {
    background: "linear-gradient(180deg, #7c3aed, #5b21b6)",
  },

  "&.awaitingUpdate": {
    background: "linear-gradient(180deg, #0f766e, #115e59)",
  },
});

const CardInner = styled("div")({
  gridColumn: 2,
  minWidth: 0,
  width: "100%",
  minHeight: "18rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
});

const CardSummary = styled("header")(({ theme }) => ({
  minHeight: "5.6rem",
  display: "grid",
  gridTemplateColumns: "minmax(26rem, 1fr) auto",
  alignItems: "center",
  gap: "1rem",
  padding: "0.9rem 1rem 0.9rem 1.1rem",
  borderBottom: `0.1rem solid ${theme.palette.common.grayBorder}`,
  background: "rgba(255,255,255,0.72)",
}));

const SummaryPrimary = styled("div")({
  minWidth: 0,
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  gridTemplateRows: "auto auto",
  alignItems: "center",
  columnGap: "0.75rem",
  rowGap: "0.2rem",
});

const StatusBadge = styled("div")({
  gridRow: "1 / 3",
  position: "relative",
  minWidth: "10.8rem",
  minHeight: "2.5rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0.3rem 0.8rem",
  borderRadius: "0.7rem",
  color: "#fff",
  fontSize: "0.82rem",
  fontWeight: 950,
  letterSpacing: 0,
  lineHeight: 1.12,
  textTransform: "uppercase",
  textAlign: "center",
  boxShadow: "inset 0 0 0 0.1rem rgba(255,255,255,0.12)",

  "&.awaitingAction": {
    background: "#c2410c",
  },

  "&.investigating": {
    background: "#2563eb",
  },

  "&.closing": {
    background: "#7c3aed",
  },

  "&.awaitingUpdate": {
    background: "#0f766e",
  },
});

const SummaryTitle = styled("div")({
  minWidth: 0,
  color: "#0f172a",
  fontSize: "1.18rem",
  fontWeight: 950,
  lineHeight: 1.18,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const SummarySubtitle = styled("div")(({ theme }) => ({
  minWidth: 0,
  color: theme.palette.common.grayText,
  fontSize: "0.94rem",
  fontWeight: 750,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const SummaryMetrics = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(5, max-content)",
  gap: "0.55rem",
  alignItems: "center",
});

const MetricBlock = styled("div")(({ theme }) => ({
  minWidth: "5.2rem",
  minHeight: "3.3rem",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "0.15rem",
  padding: "0.45rem 0.65rem",
  borderRadius: "0.65rem",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  background: "rgba(248,250,252,0.8)",

  "& span": {
    color: theme.palette.common.grayText,
    fontSize: "0.72rem",
    fontWeight: 900,
    letterSpacing: 0,
    textTransform: "uppercase",
  },

  "& strong": {
    color: "#0f172a",
    fontSize: "0.94rem",
    fontWeight: 950,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  },
}));

const CardBody = styled("div")({
  minWidth: 0,
  width: "100%",
  display: "flex",
  alignItems: "stretch",
});

export default Card;
